<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeMount } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';
import { bindThemeServerPersistence, useTheme } from '@/composables/useTheme';
import ToastContainer from '@/components/ToastContainer.vue';

const AuthLayout = defineAsyncComponent(() => import('@/layouts/AuthLayout.vue'));
const BlankLayout = defineAsyncComponent(() => import('@/layouts/BlankLayout.vue'));

const route = useRoute();
const auth = useAuthStore();
const { applyServerPreference } = useTheme();

onBeforeMount(async () => {
  if (auth.isAuthenticated && !auth.me) {
    try {
      await auth.loadMe();
    } catch {
      /* router guard will redirect */
    }
  }
  if (auth.me?.user.theme_preference) {
    applyServerPreference(auth.me.user.theme_preference);
  }
});

bindThemeServerPersistence((mode) => auth.persistThemePreference(mode));

const layoutName = computed<'default' | 'auth' | 'blank'>(() => {
  return route.meta.layout ?? 'default';
});
</script>

<template>
  <!--
    DefaultLayout owns its own <RouterView> as a child route system because
    nested children are routed through it (see router/index.ts). For 'auth'
    and 'blank' layouts we wrap the matched component using <component :is>.
  -->
  <RouterView v-if="layoutName === 'default'" />
  <AuthLayout v-else-if="layoutName === 'auth'">
    <RouterView />
  </AuthLayout>
  <BlankLayout v-else>
    <RouterView />
  </BlankLayout>
  <ToastContainer />
</template>
